import { Dropdown, Menu, Radio } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import {
  BulkActionActiveIcon as bulkActionIconActive,
  BulkActionIcon as bulkActionIcon,
} from '@/shared/ui';
import { BULK_ACTION_ENUM } from '@/shared/model';
import useBulkAction from './useBulkAction';
import './styles.scss';

export type BulkActionProps = {
  isDisabled?: boolean;

  keyRecoil: string;

  tabId?: string;

  isTemplate?: boolean;
};

/**
 * Renders the page-level bulk action dropdown for the beats tab, letting the reviewer apply one action to every beat on the current page. Builds radio menu items from the bulkActionMenu ref supplied by useBulkAction, and marks the trigger active with a highlighted icon and label whenever the selected action is not NOTHING.
 *
 * @param props - Bulk action inputs forwarded to useBulkAction, which derives the menu, current action and disabled state.
 * @returns The bulk action dropdown with its trigger button and hover tooltip.
 */
const BulkAction = ({
  isDisabled: isDisabledProp,
  keyRecoil,
  tabId,
  isTemplate,
}: BulkActionProps) => {
  const { isDisabled, bulkActionMenu, bulkAction, onClickItem, getBulkActionText } = useBulkAction({
    isDisabled: isDisabledProp,
    keyRecoil,
    tabId,
    isTemplate,
  });

  const items = _.map(bulkActionMenu.current, (x, i) => ({
    key: `bulk-action-${i}`,
    label: (
      <Radio
        key={i}
        checked={x.value === bulkAction}
        value={x.value}
        className="bulk-action-radio"
        id={`beat-bulk-action-${i}`}
      >
        <div className="bulk-action-item">
          <div className="item-label">{x.label}</div>
          <div className="item-hotkey">{x.hotkey}</div>
        </div>
      </Radio>
    ),
    value: x.value,
  }));

  return (
    <Dropdown
      trigger={['click']}
      disabled={isDisabled}
      overlay={
        <div className="bulk-action-menu-wrapper">
          <div className="bulk-action-menu-desc">Do an action to all beats in a page:</div>
          <Menu onClick={onClickItem} className="bulk-action-menu" items={items} />
        </div>
      }
    >
      <div className="bulk-action-wrapper">
        <button
          id="bulk-action-text---rr-heatmap"
          disabled={isDisabled}
          className={classNames(
            'bulk-action-text --rr-heatmap',
            bulkAction === BULK_ACTION_ENUM.NOTHING ? '' : '--active',
          )}
        >
          <img
            src={bulkAction === BULK_ACTION_ENUM.NOTHING ? bulkActionIcon : bulkActionIconActive}
            alt="bulk action icon"
          />
          <span className="bulk-action-label">{getBulkActionText(bulkAction)}</span>
        </button>
        <UncontrolledTooltip
          className="bulk-action-tooltip custom-tooltip"
          target="bulk-action-text---rr-heatmap"
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
          trigger="hover"
        >
          <div className="beat-tooltip">Page actions</div>
        </UncontrolledTooltip>
      </div>
    </Dropdown>
  );
};

export default BulkAction;
